/**
 * Shared Axios client: base URL, Firebase token, Nest error mapping.
 */

import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { signOut } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { useAuthStore } from '@/stores/authStore';
import {
  getErrorMessage,
  isTutorPlatformAccessDeniedMessage,
  pickNestMessage,
  mapBackendMessage,
} from '@/utils/errors-utils';
import { getApiV1BaseUrlFromEnv } from '@/lib/apiBaseUrl';

/** Axios request shape used by the 401 retry path. */
type RetryableRequest = {
  url?: string;
  headers?: { Authorization?: string };
  _retry?: boolean;
};

/** Axios instance: `VITE_API_URL` base, Firebase bearer, Nest error translation. */
const apiClient = axios.create({
  baseURL: getApiV1BaseUrlFromEnv(import.meta.env.VITE_API_URL),
  timeout: Number.parseInt(import.meta.env.VITE_API_TIMEOUT || '30000', 10),
});

let refreshPromise: Promise<string | null> | null = null;

/** Single-flight `getIdToken(true)` so concurrent 401s share one refresh. */
const refreshTokenOnce = (): Promise<string | null> => {
  refreshPromise ??= (async () => {
    try {
      return (await auth.currentUser?.getIdToken(true)) ?? null;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

/** True when the outgoing request already carried a Bearer token. */
const requestHadAuthorization = (config: { headers?: unknown } | undefined): boolean => {
  const headers = config?.headers;
  if (!headers) return false;
  const withGet = headers as { get?: (name: string) => string | undefined };
  if (typeof withGet.get === 'function') {
    return Boolean(withGet.get('Authorization') ?? withGet.get('authorization'));
  }
  const rec = headers as Record<string, string | undefined>;
  return Boolean(rec.Authorization ?? rec.authorization);
};

/** Sign out Firebase + Zustand and redirect to `/login` after a failed refresh. */
const forceLogout = async (): Promise<void> => {
  try {
    localStorage.setItem('intentional_logout', 'true');
    await signOut(auth);
    useAuthStore.getState().logout();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  } catch (logoutError) {
    console.error('[api-client] Error during forced logout:', logoutError);
  }
};

/** ID token after another tab may have rotated Firebase auth. */
const tokenFromOtherTab = async (): Promise<string | null> => {
  await auth.authStateReady();
  return (await auth.currentUser?.getIdToken()) ?? null;
};

/** Replay a 401 once with a fresh token. Returns the new response, or `null` to keep failing. */
const replayAfterUnauthorized = async (
  originalRequest: RetryableRequest | undefined,
): Promise<AxiosResponse | null> => {
  if (!originalRequest || originalRequest.url?.includes('/auth/logout')) return null;
  if (!requestHadAuthorization(originalRequest)) return null;
  if (originalRequest._retry) {
    await forceLogout();
    return null;
  }

  originalRequest._retry = true;
  const token = (await refreshTokenOnce()) ?? (await tokenFromOtherTab());
  if (!token) {
    await forceLogout();
    return null;
  }

  originalRequest.headers = originalRequest.headers ?? {};
  originalRequest.headers.Authorization = `Bearer ${token}`;
  return apiClient(originalRequest as AxiosRequestConfig);
};

/** Flag tutor 403 so the login page can show the staff-app denial. */
const flagTutorBlockedIfDenied = (data: unknown): void => {
  const errorMessage = pickNestMessage(data) ?? '';
  if (!isTutorPlatformAccessDeniedMessage(errorMessage)) return;
  sessionStorage.setItem('tutor_blocked', 'true');
  sessionStorage.setItem('tutor_blocked_message', errorMessage);
};

/** Interceptor for requests to the API. */
apiClient.interceptors.request.use(
  async (config) => {
    let user = auth.currentUser;

    // After sleep/tab wake, Firebase may not have resolved auth.currentUser yet.
    if (!user && useAuthStore.getState().isAuthenticated) {
      await auth.authStateReady();
      user = auth.currentUser;
    }

    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('[api-client] No Firebase user found, request will likely fail with 401');
    }

    // Identifier staff web app for backend access control
    config.headers['X-App-Platform'] = 'staff-web';

    // Backend expects /companies/:id (UUID), not "current" — swap in the user's companyId.
    const url = config.url ?? '';
    if (url.includes('companies/current')) {
      const companyId = useAuthStore.getState().user?.companyId;
      if (companyId) {
        config.url = url.replaceAll('companies/current', `companies/${companyId}`);
      } else {
        console.error('[api-client] companyId missing — request blocked.', {
          url,
          user: useAuthStore.getState().user,
        });
        throw new Error(getErrorMessage('companyContextUnavailable'));
      }
    }

    return config;
  },
  (error) => {
    throw error;
  },
);

/** Interceptor for responses from the API. */
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetryableRequest | undefined;

    if (error.response?.status === 401) {
      const replayed = await replayAfterUnauthorized(originalRequest);
      if (replayed) return replayed;
    }

    if (error.response?.status === 403) {
      flagTutorBlockedIfDenied(error.response?.data);
    }

    if (error.response?.data?.message) {
      error.response.data.message = mapBackendMessage(error.response.data.message);
    }

    throw error;
  },
);

export { apiClient };
export default apiClient;
